import { useState, useContext, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import Dropzone from "react-dropzone";
import CloseIcon from "@mui/icons-material/Close";
import UploadIcon from "@mui/icons-material/Upload";
import { RootContext } from "../context/root-provider";
import AppBackService from "../services/appback";
import axios from "axios";

const styles = {
  uploadBtn: {
    position: "absolute",
    right: 8,
    top: 12,
  },
  container: {
    padding: "20px",
  },
  attachDocText: {
    marginBottom: "8px",
    fontWeight: "bold",
  },
  uploadIconStyles: {
    color: "var(--color-accent-primary)",
  },
  textField: {
    marginBottom: "10px",
  },
  dropZoneStyle: {
    minWidth: { md: "290px" },
    boxSizing: "border-box",
    textAlign: "center",
    padding: "20px",
    border: "3px dashed var(--color-accent-primary)",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      background: "var(--color-interactive-hover)",
    },
    "& p": {
      margin: "8px 0",
      lineHeight: "24px",
      color: "var(--color-text-secondary)",
    },
  },
  clickCta: {
    color: "var(--color-accent-primary)",
    textDecoration: "underline",
    fontWeight: 600,
  },
};

// Map file extensions to content types
const CONTENT_TYPE_MAP = {
  ".mp3": "audio/mpeg",
  ".m4a": "audio/mp4",
  ".wav": "audio/wav",
  ".ogg": "audio/ogg",
  ".flac": "audio/flac",
};

const UploadAudioModal = ({ open, setOpen, showSnackbar, refreshCallback }) => {
  const [audioFile, setAudioFile] = useState(null);
  const [audioTitle, setAudioTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const { userInfo } = useContext(RootContext);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  useEffect(() => {
    if (!open) {
      setAudioFile(null);
      setAudioTitle("");
      setUploading(false);
      setUploadProgress(0);
    }
  }, [open]);

  const handleDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setAudioFile(file);
      // Generate title from filename
      const title = file.name
        .replace(/\.[^/.]+$/, "") // Remove extension
        .replace(/[_-]/g, " ") // Replace underscores and hyphens with spaces
        .replace(/\b\w/g, (l) => l.toUpperCase()); // Capitalize first letter of each word
      setAudioTitle(title);
    }
  }, []);

  const uploadAudio = useCallback(async () => {
    if (!audioFile || !userInfo?.username) {
      showSnackbar("Please select an audio file", "error");
      return;
    }

    setUploading(true);

    try {
      // Get file extension and determine content type
      const ext = "." + audioFile.name.split(".").pop().toLowerCase();
      const contentType = CONTENT_TYPE_MAP[ext];

      if (!contentType) {
        showSnackbar(`Unsupported audio file type: ${ext}`, "error");
        setUploading(false);
        return;
      }

      showSnackbar("Creating audio document...", "info");

      // Step 1: Create audio document and get upload URL
      const createResp = await AppBackService.uploadAudio(userInfo.username, audioTitle, contentType);

      if (!createResp || createResp.error) {
        showSnackbar("Failed to create audio document", "error");
        setUploading(false);
        return;
      }

      const { docId, uploadUrl, s3Key, existingSize } = createResp;

      // Check if file already exists with correct size
      if (existingSize !== undefined && existingSize === audioFile.size) {
        showSnackbar("File already exists with correct size, skipping upload", "info");

        // Complete the upload with existing file
        const completeResp = await AppBackService.completeAudioUpload(userInfo.username, docId, s3Key, audioFile.size);

        if (!completeResp || completeResp.error) {
          showSnackbar("Failed to complete audio upload", "error");
          setUploading(false);
          return;
        }

        showSnackbar("Audio file processing started", "success");
        closeModal();
        if (refreshCallback) {
          refreshCallback(false);
        }
        setUploading(false);
        return;
      }

      // If existing_size check failed (403 error), we still proceed with upload
      showSnackbar(`Uploading audio file: ${audioFile.name}...`, "info");

      // Step 2: Upload file to S3
      const uploadResp = await axios.put(uploadUrl, audioFile, {
        headers: {
          "Content-Type": contentType,
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        },
      });

      if (uploadResp.status !== 200) {
        showSnackbar("Failed to upload audio file to S3", "error");
        setUploading(false);
        return;
      }

      showSnackbar("Audio file uploaded, finalizing...", "info");

      // Step 3: Complete the upload
      const completeResp = await AppBackService.completeAudioUpload(userInfo.username, docId, s3Key, audioFile.size);

      if (!completeResp || completeResp.error) {
        showSnackbar("Failed to complete audio upload", "error");
        setUploading(false);
        return;
      }

      showSnackbar("Audio file uploaded successfully. Transcription started.", "success");
      closeModal();
      if (refreshCallback) {
        refreshCallback(false);
      }
    } catch (error) {
      console.error("Upload error:", error);
      showSnackbar("Error uploading audio file", "error");
    } finally {
      setUploading(false);
    }
  }, [audioFile, audioTitle, closeModal, refreshCallback, showSnackbar, userInfo]);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={closeModal}>
      <DialogTitle>
        Upload Audio File
        <IconButton aria-label="close" onClick={closeModal} sx={styles.uploadBtn} disabled={uploading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {uploading && uploadProgress > 0 && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2">Uploading...</Typography>
            <Typography variant="body2">{uploadProgress}%</Typography>
          </Box>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}
      <Box sx={styles.container}>
        <Typography sx={styles.attachDocText}>Select an audio file</Typography>
        <Dropzone
          onDrop={handleDrop}
          multiple={false}
          accept={{
            "audio/mpeg": [".mp3"],
            "audio/mp4": [".m4a"],
            "audio/wav": [".wav"],
            "audio/ogg": [".ogg"],
            "audio/flac": [".flac"],
          }}
          disabled={uploading}>
          {({ getRootProps, getInputProps }) => (
            <section>
              <Box {...getRootProps()} sx={styles.dropZoneStyle}>
                <input {...getInputProps()} />
                <UploadIcon fontSize="large" sx={styles.uploadIconStyles} />
                <p>
                  {audioFile ? (
                    <>
                      <strong>{audioFile.name}</strong>
                      <br />
                      {(audioFile.size / 1024 / 1024).toFixed(2)} MB
                    </>
                  ) : (
                    <>
                      Drag and drop here
                      <br />
                      <span style={styles.clickCta}>or click to browse files</span>
                      <br />
                      Accepted file types: .mp3, .m4a, .wav, .ogg, .flac
                    </>
                  )}
                </p>
              </Box>
            </section>
          )}
        </Dropzone>
      </Box>
      <Box component="form" sx={styles.container}>
        <div>
          <TextField
            id="audio-title"
            placeholder="Audio title"
            value={audioTitle}
            onChange={(e) => setAudioTitle(e.target.value)}
            sx={styles.textField}
            fullWidth
            disabled={uploading}
          />
        </div>
      </Box>
      <DialogActions>
        <Button onClick={closeModal} disabled={uploading}>
          Cancel
        </Button>
        <Button onClick={uploadAudio} disabled={!audioFile || !audioTitle || uploading}>
          {uploading ? "Uploading..." : "Upload Audio"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

UploadAudioModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  showSnackbar: PropTypes.func.isRequired,
  refreshCallback: PropTypes.func,
};

export default UploadAudioModal;
